import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
  renderEdit,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Contact,
  renderCheckbox,
  renderSelect,
  DisplayData,
  getCountries,
  getStateProvinces,
  DisplayDataStateProvince,
  stateProvinceAbbreviationToObject,
  getPhonePrefixForCountry,
  countryIso2ToIso3,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import ContactItemProps from './ContactItemProps.interface';
import '../../customer-info.scss';

const maxEmailLength = 100;
const maxAddressLength = 100;

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlStyle: ControlStyleClass = null;

const ContactItem = ({
  contact,
  onUpdateContact,
  primaryCanChange,
}: ContactItemProps): JSX.Element => {
  const key = contact.uuid;
  Logging.Debug('Contact', key, contact);

  const [primaryInd, setPrimaryInd] = React.useState(contact.primaryInd);
  const [name, setName] = React.useState(contact.name);
  const [department, setDepartment] = React.useState(contact.department);
  const [email, setEmail] = React.useState(contact.email);
  const [phone, setPhone] = React.useState(contact.phone);
  const [phoneExtension, setPhoneExtension] = React.useState(contact.phoneExtension);
  const [fax, setFax] = React.useState(contact.fax);
  const [address, setAddress] = React.useState(contact.address);
  const [address2, setAddress2] = React.useState(contact.address2);
  const [city, setCity] = React.useState(contact.city);
  const [state, setState] = React.useState(contact.state);
  const [postalCode, setPostalCode] = React.useState(contact.postalCode);
  const [country, setCountry] = React.useState(countryIso2ToIso3(contact.country));
  const [phonePrefix, setPhonePrefix] = React.useState(getPhonePrefixForCountry(contact.country));

  const controlValidation: ControlValidation = {
    requiredFields: [`name-${key}`],
    emailFields: [`email-${key}`],
    phoneFields: [`phone-${key}`, `fax-${key}`],
    phoneExtensionFields: [`phoneExtension-${key}`],
    postalCodeFields: [`postalCode-${key}`],
    minMaxSize: [
      { field: `email-${key}`, maxSize: maxEmailLength },
      { field: `address-${key}`, maxSize: maxAddressLength },
      { field: `address2-${key}`, maxSize: maxAddressLength },
    ],
    customMessages: null,
  };

  const updateValues = (): Contact => {
    const updatedContact: Contact = {
      uuid: contact.uuid,
      name,
      department,
      email,
      phone,
      phoneExtension,
      fax,
      address,
      address2,
      city,
      state,
      postalCode,
      country,
      primaryInd,
      emailSubscribedInd: contact.emailSubscribedInd,
    };

    return updatedContact;
  };

  // Country was Changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // Country was Changed.  Check to see if the State is still Valid
    if (country && state) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

      // Clear the State if a differnt Country was picked
      if (displayDataGeo && (country !== displayDataGeo.country)) {
        setState(null);
      }
    } else if (!country && state) {
      setState(null);
    }

    // Set the Phone Prefix
    setPhonePrefix(getPhonePrefixForCountry(country));
  }, [country]);

  // State was changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // State is Selected but not the Country.
    if (state && !country) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

      // Picking the Country from the State
      if (displayDataGeo) {
        setCountry(displayDataGeo.country);
      }
    }
  }, [state]);

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    const updatedContact: Contact = updateValues();

    // Check to see if the Original Contact was modified
    if (!_.isEqual(contact, updatedContact)) {
      Logging.Debug('Not Equal', key, contact, updatedContact);
      onUpdateContact(updatedContact);
    }
  });

  // Set the Country used in Validation
  controlValidation.country = country;

  return (
    <div className="contactContainer">
      {renderEdit(25, `name-${key}`, 'Contact Name*', 'text', null,
        name, setName, controlStateEnabled, controlStyle, controlValidation, null)}
      {renderEdit(25, `department-${key}`, 'Department', 'text', null,
        department, setDepartment, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `email-${key}`, 'E-Mail', 'email', null, email, setEmail, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `phone-${key}`, 'Phone Number', 'tel', '(xxx) xxx-xxxx',
        phone, setPhone, controlStateEnabled, controlStyle, controlValidation, phonePrefix)}
      {renderEdit(25, `phoneExtension-${key}`, 'Extension', 'text', null,
        phoneExtension, setPhoneExtension, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `fax-${key}`, 'Fax Number', 'tel', '(xxx) xxx-xxxx',
        fax, setFax, controlStateEnabled, controlStyle, controlValidation, phonePrefix)}
      <div className="field25" />
      <div className="field25" />
      {renderEdit(25, `address-${key}`, 'Street Address', 'text', null,
        address, setAddress, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `address2-${key}`, 'Street Address 2', 'text', null,
        address2, setAddress2, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `city-${key}`, 'City', 'text', null, city, setCity, controlStateEnabled, controlStyle, controlValidation)}
      {renderSelect(25, `state-${key}`, 'State / Province', 'Select State / Province', false, false,
        getStateProvinces(country), true, null, state, setState, controlStateEnabled, controlStyle, controlValidation)}
      {renderEdit(25, `postalCode-${key}`, 'Postal / Zip Code', 'text', null,
        postalCode, setPostalCode, controlStateEnabled, controlStyle, controlValidation)}
      {renderSelect(25, `country-${key}`, 'Country', 'Select Country', false, false,
        getCountries(), true, null, country, setCountry, controlStateEnabled, controlStyle, controlValidation)}
      <div className="field25 primaryIndicator">
        {renderCheckbox(`primaryInd-${key}`, 'Primary Contact', primaryInd, null,
          setPrimaryInd, primaryCanChange ? controlStateEnabled : controlStateDisabled, 'customerCheckbox')}
      </div>
    </div>
  );
};

export default ContactItem;
